import parse from 'html-react-parser';
import { isArray, isEmpty } from 'radash';

import { PersistedItem } from '@/types/DataModel';

import { useItemHierarchyName } from '../hooks/useItemHierarchyName';
import { useItemTitle } from '../hooks/useItemTitle';
import { useItemHierarchy, useItems } from '../ItemEditor.queries';

interface ItemEditorTitleProps {
    item: PersistedItem | undefined;
    isCreatingNewItem: boolean;
}

const ItemEditorTitle = ({ item, isCreatingNewItem }: ItemEditorTitleProps) => {
    const { itemHierarchyName, isLoading: isItemHierarchyNameLoading } = useItemHierarchyName(
        item || ({} as PersistedItem),
    );

    const { data: hierarchiesData, isLoading: isHierarchyDataLoading } = useItemHierarchy({
        variables: {
            primaryKey__: item?.primaryKey__ || '',
            hierarchyName: itemHierarchyName || '',
        },
        enabled: !isEmpty(item?.primaryKey__) && !isEmpty(itemHierarchyName) && !isItemHierarchyNameLoading,
    });

    const items = isArray(hierarchiesData) ? hierarchiesData.map((element) => element?.primaryKey) : [];

    const { data: allItemsData, isLoading: isAllItemsDataLoading } = useItems({
        variables: { primaryKeys: items },
        enabled: !isEmpty(items),
    });

    const baseHierarchyElement = isArray(hierarchiesData) ? hierarchiesData?.[0] : null;
    const baseItemData =
        allItemsData?.find((it: PersistedItem) => it.primaryKey__ === baseHierarchyElement?.primaryKey) || item;

    const { title } = useItemTitle({ isCreatingNewItem, baseItemData });

    const isLoading =
        !isCreatingNewItem &&
        !isEmpty(item?.primaryKey__) &&
        (isItemHierarchyNameLoading || isHierarchyDataLoading || isAllItemsDataLoading);

    if (isLoading && isEmpty(title)) {
        return (
            <div
                className="tw-h-6 tw-w-24 tw-animate-pulse tw-rounded tw-bg-fill-secondary"
                data-testid="item_title"
            />
        );
    }

    return (
        <div
            className="tw-min-w-0 tw-truncate tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg"
            data-testid="item_title"
        >
            {parse(title)}
        </div>
    );
};

ItemEditorTitle.displayName = 'ItemEditorTitle';

export { ItemEditorTitle };
